import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../../shared/components/Table/Table";
import { ExternalLink, Trash2, FileText, FileUp, Eye } from "lucide-react";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";
import StatusFilterDropdown from "../../learnerControl/components/StatusFilterDropdown";
import JobOpportunityClient from "../data/api/JobOpportunityClient";
import "../../learnerControl/styles/LearnerStyles.css";
import "../JobOpportunity.css";

const JD_STATUS_FILTER_OPTIONS = [
  { value: "uploading", label: "Uploading", dotColor: "#f59e0b" },
  { value: "processing", label: "Processing", dotColor: "#8b5cf6" },
  { value: "parsed", label: "Parsed", dotColor: "#059669" },
  { value: "failed", label: "Failed", dotColor: "#ef4444" },
];

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getFileExt = (name: string) => {
  const parts = name.split(".");
  return parts.length > 1 ? `.${parts[parts.length - 1].toUpperCase()}` : "FILE";
};

const UploadJDPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [trackerRecords, setTrackerRecords] = useState<any[]>([]);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerPage, setTrackerPage] = useState(1);
  const [trackerHasMore, setTrackerHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const observer = useRef<IntersectionObserver | null>(null);
  const lastRowRef = useCallback(
    (node: any) => {
      if (trackerLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && trackerHasMore) setTrackerPage((p) => p + 1);
      });
      if (node) observer.current.observe(node);
    },
    [trackerLoading, trackerHasMore],
  );

  const fetchTrackerRecords = useCallback(async (page = 1, isLoadMore = false) => {
    setTrackerLoading(true);
    try {
      const data = await JobOpportunityClient.trackUploads({ limit: 15, start_record: (page - 1) * 15 });
      const newRecords = data.records || [];
      if (isLoadMore) {
        setTrackerRecords((prev) => {
          const ids = new Set(prev.map((r: any) => r.file_id));
          return [...prev, ...newRecords.filter((r: any) => !ids.has(r.file_id))];
        });
      } else {
        setTrackerRecords(newRecords);
      }
      setTrackerHasMore(newRecords.length === 15);
    } catch { /* silent */ } finally {
      setTrackerLoading(false);
    }
  }, []);

  useEffect(() => {
    trackerPage === 1 ? fetchTrackerRecords(1, false) : fetchTrackerRecords(trackerPage, true);
  }, [trackerPage, fetchTrackerRecords]);

  useEffect(() => {
    const hasPending = trackerRecords.some((r) => r.status === "uploading" || r.status === "processing");
    if (!hasPending) return;
    const id = setInterval(() => fetchTrackerRecords(1, false), 5000);
    return () => clearInterval(id);
  }, [trackerRecords, fetchTrackerRecords]);

  const handleFileSelect = (file: File) => {
    const ext = file.name.toLowerCase();
    if (!ext.endsWith(".pdf") && !ext.endsWith(".docx") && !ext.endsWith(".txt")) {
      setError("Only PDF, DOCX, or TXT files are allowed");
      return;
    }
    setSelectedFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFileSelect(f);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelect(f);
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError("Please select a JD file"); return; }
    setUploading(true);
    setError(null);
    try {
      await JobOpportunityClient.extractFromDocument(selectedFile);
      setInfoMessage("JozAI is parsing the JD. Check the tracker below for progress.");
      setSelectedFile(null);
      setTrackerPage(1);
      fetchTrackerRecords(1, false);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "JD upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const getTrackerPill = (status: string, note?: string) => {
    const s = status?.toLowerCase() || "";
    let pillClass = "lp-badge pending";
    if (s === "parsed") pillClass = "lp-badge active";
    else if (s === "failed") pillClass = "lp-badge inactive";
    return (
      <span className={pillClass}
        style={{ display: "inline-flex", padding: "4px 10px", borderRadius: "12px", alignItems: "center", gap: 6 }}
        title={note || ""}>
        <span className="lp-badge-dot" />
        {status}
        {(s === "failed" || s === "error") && (
          <span style={{ cursor: "help" }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
        )}
      </span>
    );
  };

  const filteredRecords = trackerRecords.filter((r) => {
    const ms = searchQuery ? r.file_name?.toLowerCase().includes(searchQuery.toLowerCase()) : true;
    const mst = statusFilter !== "all" ? (r.status || "").toLowerCase() === statusFilter : true;
    return ms && mst;
  });

  const parsedCount = trackerRecords.filter((r) => r.status === "parsed").length;
  const processingCount = trackerRecords.filter((r) => r.status === "uploading" || r.status === "processing").length;
  const failedCount = trackerRecords.filter((r) => r.status === "failed").length;

  return (
    <>
      <Loader isLoading={uploading} message="Uploading JD..." />
      <Alert show={!!error} title="Error" message={error || ""} type="error" onClose={() => setError(null)} primaryButtonText="OK" />
      <Alert show={!!infoMessage} title="Upload Queued" message={infoMessage || ""} type="info" onClose={() => setInfoMessage(null)} primaryButtonText="OK" />

      <div className="upload-page-wrapper">
        <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>

          {/* ── Hero ── */}
          <div className="up-hero">
            <div className="up-hero-left">
              <button className="up-back-btn" onClick={() => navigate("/dashboard/job-opportunities")}>
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m7-7l-7 7 7 7" />
                </svg>
              </button>
              <h1>Upload Job Details</h1>
            </div>
          </div>

          {/* ── Upload Card (2-column layout, green theme) ── */}
          <div style={{
            background: "#fff",
            borderRadius: "16px",
            border: "1px solid #d1fae5",
            boxShadow: "0 2px 16px rgba(5,150,105,0.06)",
            padding: "20px",
            marginBottom: "20px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            alignItems: "stretch",
          }}>

            {/* Left Column: Drop Zone */}
            <div
              onClick={() => !uploading && fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragOver ? "#059669" : "#a7f3d0"}`,
                borderRadius: "12px",
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                cursor: uploading ? "default" : "pointer",
                background: dragOver ? "rgba(5,150,105,0.05)" : "linear-gradient(180deg, #f0fdf4 0%, #f8fffe 100%)",
                transition: "all 0.2s",
                height: "100%",
                minHeight: "140px",
              }}
            >
              <input type="file" ref={fileInputRef} onChange={handleFileInputChange} accept=".pdf,.docx,.txt" style={{ display: "none" }} />

              {/* Upload Icon */}
              <div style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(5, 150, 105, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#059669",
              }}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>

              <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "#1e293b", textAlign: "center", marginTop: "2px" }}>
                Drag and Drop file to upload
              </div>
              
              <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, margin: "-2px 0" }}>or</div>

              <button
                onClick={(e) => { e.stopPropagation(); !uploading && fileInputRef.current?.click(); }}
                disabled={uploading}
                style={{
                  background: "#059669",
                  color: "#fff",
                  border: "none",
                  borderRadius: "20px",
                  padding: "4px 16px",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  cursor: uploading ? "not-allowed" : "pointer",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => { if (!uploading) e.currentTarget.style.background = "#047857"; }}
                onMouseLeave={(e) => { if (!uploading) e.currentTarget.style.background = "#059669"; }}
              >
                Browse
              </button>

              <div style={{ fontSize: "0.7rem", color: "#94a3b8", marginTop: "2px" }}>
                Supported files: PDF, DOCX, TXT
              </div>
            </div>

            {/* Right Column: Uploaded Files & Actions */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h3 style={{ fontSize: "0.95rem", fontWeight: 700, color: "#1e293b", marginBottom: "12px" }}>
                Selected file
              </h3>

              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <AnimatePresence mode="wait">
                  {selectedFile ? (
                    <motion.div
                      key="file-card"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        padding: "12px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                        flex: 1,
                      }}
                    >
                      <div style={{
                        width: 38,
                        height: 44,
                        borderRadius: "6px",
                        background: "rgba(5, 150, 105, 0.08)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        border: "1px solid rgba(5, 150, 105, 0.2)",
                      }}>
                        <span style={{ fontSize: "0.6rem", fontWeight: 800, color: "#059669", letterSpacing: "0.5px" }}>
                          {getFileExt(selectedFile.name).replace(".", "")}
                        </span>
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {selectedFile.name}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 2 }}>
                          {formatFileSize(selectedFile.size)}
                        </div>
                      </div>

                      <button
                        onClick={() => setSelectedFile(null)}
                        disabled={uploading}
                        title="Remove file"
                        style={{
                          width: 28, height: 28, borderRadius: "6px",
                          border: "none", background: "transparent", color: "#ef4444",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: uploading ? "not-allowed" : "pointer",
                          flexShrink: 0, transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="empty-state"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f8fafc",
                        border: "1px dashed #cbd5e1",
                        borderRadius: "10px",
                        color: "#94a3b8",
                        fontSize: "0.85rem",
                        padding: "16px",
                        textAlign: "center"
                      }}
                    >
                      <FileText size={24} color="#cbd5e1" style={{ marginBottom: "8px" }} />
                      No file selected yet
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Upload Button */}
              <div style={{ marginTop: "16px" }}>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !selectedFile}
                  style={{
                    width: "100%",
                    height: 40,
                    borderRadius: "8px",
                    border: "none",
                    background: !selectedFile || uploading ? "#e2e8f0" : "#059669",
                    color: !selectedFile || uploading ? "#94a3b8" : "#fff",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    cursor: !selectedFile || uploading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: selectedFile && !uploading ? "0 4px 12px rgba(5,150,105,0.25)" : "none",
                    transition: "all 0.2s",
                    letterSpacing: "0.2px",
                  }}
                  onMouseEnter={(e) => { if (selectedFile && !uploading) { e.currentTarget.style.background = "#047857"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
                  onMouseLeave={(e) => { if (selectedFile && !uploading) { e.currentTarget.style.background = "#059669"; e.currentTarget.style.transform = "translateY(0)"; } }}
                >
                  {uploading ? (
                    <>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ animation: "spin 1s linear infinite" }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FileUp size={16} strokeWidth={2.5} />
                      Upload Job Details
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ── Job Details Upload Tracker ── */}
          <div className="up-tracker-panel">
            <div className="up-tracker-head">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h2>Job Details Upload Tracker</h2>
                <span className="up-stat-chip total">{trackerRecords.length} Total</span>
                <span className="up-stat-chip success">{parsedCount} Parsed</span>
                <span className="up-stat-chip processing">{processingCount} Processing</span>
                <span className="up-stat-chip failed">{failedCount} Failed</span>
                <button
                  onClick={() => { trackerPage === 1 ? fetchTrackerRecords(1, false) : setTrackerPage(1); }}
                  style={{ background: "transparent", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", alignItems: "center" }}
                  title="Refresh"
                >
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
              <div className="up-tracker-head-right">
                <div className="lp-search" style={{ width: 200, position: "relative" }}>
                  <svg width="14" height="14" fill="none" stroke="#94a3b8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ fontSize: "0.8rem", paddingRight: searchQuery ? "30px" : "10px" }}
                  />
                  {searchQuery && (
                    <button
                      style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", border: "none", background: "#f1f5f9", cursor: "pointer", color: "#64748b", width: "20px", height: "20px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                      onClick={() => setSearchQuery("")}
                    >
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <StatusFilterDropdown
                  value={statusFilter}
                  allLabel="All Status"
                  placeholder="Filter by status"
                  options={JD_STATUS_FILTER_OPTIONS}
                  onChange={setStatusFilter}
                />
              </div>
            </div>

            <div className="lp-table-scroll-area">
              {filteredRecords.length === 0 ? (
                <div className="table-empty">
                  <svg width="40" height="40" fill="none" stroke="#cbd5e1" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>No upload records found</span>
                  {(searchQuery || statusFilter !== "all") && (
                    <span style={{ fontSize: "0.8rem" }}>Try adjusting your filters</span>
                  )}
                </div>
              ) : (
                <Table className="learner-table">
                  <TableHeader>
                    <TableRow style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <TableHead style={{ textAlign: "left" }}>File Name</TableHead>
                      <TableHead style={{ textAlign: "left" }}>Status</TableHead>
                      <TableHead style={{ textAlign: "left" }}>Uploaded At</TableHead>
                      <TableHead className="actions-header" style={{ textAlign: "left" }}>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.map((row, i) => {
                      const isLast = i === filteredRecords.length - 1;
                      const opportunityId = row.docs?.[0]?.opportunity_id;
                      return (
                        <TableRow key={row.file_id || i} ref={isLast ? lastRowRef : undefined} style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <TableCell style={{ textAlign: "left" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <svg width="20" height="20" fill="none" stroke="#059669" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <span style={{ fontWeight: 600, color: "#334155", fontSize: "0.85rem" }}>{row.file_name || "—"}</span>
                            </div>
                          </TableCell>
                          <TableCell style={{ textAlign: "left" }}>
                            {getTrackerPill(row.status, row.status_note)}
                          </TableCell>
                          <TableCell style={{ textAlign: "left" }}>
                            <span style={{ color: "#64748b", fontSize: "0.82rem", fontWeight: 500 }}>{row.upload_time || "—"}</span>
                          </TableCell>
                          <TableCell className="actions-cell" style={{ textAlign: "left" }}>
                            {opportunityId && row.status === "parsed" && (
                              <button
                                className="action-btn view"
                                onClick={() => navigate(`/dashboard/job-opportunity/edit/${opportunityId}`)}
                                title="Review Job Opportunity"
                                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#059669", display: "flex", alignItems: "center" }}
                              >
                                <Eye size={20} />
                              </button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {filteredRecords.length > 0 && trackerHasMore && <div style={{ height: "20px" }} />}
            </div>
          </div>

        </motion.div>
      </div>
    </>
  );
};

export default UploadJDPage;
